import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SoftwareCard from '@/components/feature/SoftwareCard';
import DetailGallery from '@/pages/detail/components/DetailGallery';
import CommentSection from '@/pages/detail/components/CommentSection';
import { softwares } from '@/mocks/softwares';
import { screenshotsByCategory } from '@/mocks/screenshots';
import { formatDownloads, formatDate } from '@/utils/format';
import useDocumentMeta from '@/hooks/useDocumentMeta';

const platformIcons: Record<string, string> = {
  iOS: 'ri-apple-line',
  Android: 'ri-android-line',
  Windows: 'ri-windows-line',
  macOS: 'ri-mac-line',
  Web: 'ri-global-line',
};

export default function DetailPage() {
  const { softwareId } = useParams<{ softwareId: string }>();
  const software = softwares.find((s) => s.id === softwareId);
  const [toast, setToast] = useState('');

  useDocumentMeta({
    title: software
      ? `${software.name}免费下载及介绍 - 黄金网站软件app免费`
      : '软件详情 - 黄金网站软件app免费',
    description: software
      ? `${software.summary} 提供${software.name}免费下载、功能介绍与软件规格，当前版本${software.version}，支持${software.platform}。`
      : '黄金网站软件app免费软件详情页，了解软件的下载信息与功能介绍。',
    keywords: software
      ? `黄金网站软件app免费,${software.name},${software.categoryName},${software.tags.join(',')}`
      : '黄金网站软件app免费,黄金投资软件下载',
  });

  useEffect(() => {
    setToast('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [softwareId]);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(''), 3200);
    return () => clearTimeout(timer);
  }, [toast]);

  if (!software) {
    return (
      <SiteLayout>
        <Breadcrumb items={[{ label: '软件下载', path: '/softwares' }, { label: '软件不存在' }]} />
        <div className="rounded-lg border border-background-200 bg-background-50 p-12 text-center">
          <span className="w-16 h-16 mx-auto rounded-full bg-background-100 flex items-center justify-center text-foreground-400">
            <i className="ri-emotion-sad-line text-3xl" />
          </span>
          <h1 className="mt-4 font-heading text-lg font-semibold text-foreground-950">
            页面不存在
          </h1>
          <p className="mt-2 text-sm text-foreground-500">
            未找到对应的软件，它可能已下架或链接有误。
          </p>
          <Link
            to="/softwares"
            className="mt-5 inline-flex h-10 px-5 rounded-md bg-primary-500 hover:bg-primary-600 text-secondary-950 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors items-center justify-center"
          >
            返回软件列表
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const screenshots = [software.cover, ...(screenshotsByCategory[software.categoryId] ?? [])];
  const platforms = software.platform.split(' / ').map((p) => p.trim());
  const related = softwares
    .filter((s) => s.categoryId === software.categoryId && s.id !== software.id)
    .slice(0, 3);
  const fallbackRelated = softwares.filter((s) => s.id !== software.id).slice(0, 3);
  const recommend = related.length > 0 ? related : fallbackRelated;

  const handleDownload = () => {
    setToast('演示提示：本站不提供真实下载，下载地址为示范链接，请前往软件官方网站获取正式版本。');
  };

  return (
    <SiteLayout>
      <Breadcrumb
        items={[
          { label: '软件下载', path: '/softwares' },
          { label: software.categoryName, path: `/category/${software.categoryId}` },
          { label: software.name },
        ]}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <DetailGallery images={screenshots} name={software.name} />
        </div>

        <div className="rounded-lg border border-background-200 bg-background-50 p-5 flex flex-col">
          <div className="flex items-center gap-2 flex-wrap">
            <Link
              to={`/category/${software.categoryId}`}
              className="px-2 py-0.5 rounded bg-background-100 text-foreground-600 text-xs cursor-pointer hover:text-primary-700 transition-colors"
            >
              {software.categoryName}
            </Link>
            {software.free && (
              <span className="px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 text-xs font-medium">
                免费下载
              </span>
            )}
          </div>
          <h1 className="mt-2 font-heading text-xl md:text-2xl font-bold text-foreground-950">
            {software.name}
          </h1>
          <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{software.summary}</p>

          <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
            <div className="rounded-md bg-background-100 px-3 py-2">
              <p className="text-xs text-foreground-500">版本号</p>
              <p className="mt-0.5 font-medium text-foreground-900">{software.version}</p>
            </div>
            <div className="rounded-md bg-background-100 px-3 py-2">
              <p className="text-xs text-foreground-500">文件大小</p>
              <p className="mt-0.5 font-medium text-foreground-900">{software.size}</p>
            </div>
            <div className="rounded-md bg-background-100 px-3 py-2">
              <p className="text-xs text-foreground-500">下载量</p>
              <p className="mt-0.5 font-medium text-foreground-900">
                {formatDownloads(software.downloads)}
              </p>
            </div>
            <div className="rounded-md bg-background-100 px-3 py-2">
              <p className="text-xs text-foreground-500">评分</p>
              <p className="mt-0.5 font-medium text-foreground-900 flex items-center gap-1">
                <span className="w-4 h-4 flex items-center justify-center text-primary-500">
                  <i className="ri-star-fill text-sm" />
                </span>
                {software.rating.toFixed(1)}
              </p>
            </div>
          </div>

          <p className="mt-3 text-xs text-foreground-500">
            发布日期：{formatDate(software.publishDate)} · 支持平台：{software.platform}
          </p>

          <div className="mt-5">
            <button
              type="button"
              onClick={handleDownload}
              className="w-full h-11 rounded-md bg-accent-500 hover:bg-accent-600 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors flex items-center justify-center gap-2"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-download-line text-base" />
              </span>
              免费下载
            </button>
          </div>

          <div className="mt-4 flex items-center gap-3 rounded-md border border-dashed border-background-300 p-3">
            <span className="w-16 h-16 rounded-md bg-background-100 flex items-center justify-center text-foreground-500 shrink-0">
              <i className="ri-qr-code-line text-3xl" />
            </span>
            <div>
              <p className="text-sm font-medium text-foreground-900">扫码下载（演示）</p>
              <p className="mt-1 text-xs text-foreground-500 leading-relaxed">
                演示环境不生成真实二维码，正式版本可在此展示移动端下载码。
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 flex flex-col gap-6">
          <section className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
            <h2 className="font-heading text-lg font-bold text-foreground-950 flex items-center gap-2">
              <span className="w-5 h-5 flex items-center justify-center text-primary-600">
                <i className="ri-function-line text-xl" />
              </span>
              功能介绍
            </h2>
            <ul className="mt-4 flex flex-col gap-3">
              {software.features.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text-foreground-700 leading-relaxed">
                  <span className="w-4 h-4 mt-0.5 flex items-center justify-center text-primary-600 shrink-0">
                    <i className="ri-checkbox-circle-line text-base" />
                  </span>
                  {f}
                </li>
              ))}
            </ul>
          </section>

          <CommentSection softwareId={software.id} />
        </div>

        <div className="flex flex-col gap-6">
          <section className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading text-base font-bold text-foreground-950 flex items-center gap-2">
              <span className="w-5 h-5 flex items-center justify-center text-primary-600">
                <i className="ri-file-list-3-line text-xl" />
              </span>
              软件规格
            </h2>
            <dl className="mt-4 flex flex-col divide-y divide-background-200 text-sm">
              {[
                { label: '软件名称', value: software.name },
                { label: '当前版本', value: software.version },
                { label: '文件大小', value: software.size },
                { label: '支持平台', value: software.platform },
                { label: '最新更新', value: formatDate(software.publishDate) },
                { label: '收费方式', value: software.free ? '免费' : '付费' },
              ].map((row) => (
                <div key={row.label} className="flex items-start justify-between gap-3 py-2.5">
                  <dt className="text-foreground-500 whitespace-nowrap">{row.label}</dt>
                  <dd className="text-foreground-900 text-right">{row.value}</dd>
                </div>
              ))}
            </dl>
          </section>

          <section className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading text-base font-bold text-foreground-950 flex items-center gap-2">
              <span className="w-5 h-5 flex items-center justify-center text-primary-600">
                <i className="ri-history-line text-xl" />
              </span>
              更新日志
            </h2>
            <ul className="mt-4 flex flex-col gap-4">
              {software.updates.map((u) => (
                <li key={u.version} className="flex gap-3">
                  <span className="mt-1.5 w-2 h-2 rounded-full bg-primary-500 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-foreground-900">
                      {u.version}
                      <span className="ml-2 text-xs font-normal text-foreground-400">
                        {formatDate(u.date)}
                      </span>
                    </p>
                    <p className="mt-1 text-xs text-foreground-600 leading-relaxed">{u.note}</p>
                  </div>
                </li>
              ))}
            </ul>
          </section>

          <section className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading text-base font-bold text-foreground-950 flex items-center gap-2">
              <span className="w-5 h-5 flex items-center justify-center text-primary-600">
                <i className="ri-smartphone-line text-xl" />
              </span>
              支持的平台
            </h2>
            <div className="mt-4 flex flex-col gap-2">
              {platforms.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={handleDownload}
                  className="h-10 rounded-md border border-background-200 bg-background-100 hover:border-primary-300 text-sm text-foreground-700 whitespace-nowrap cursor-pointer transition-colors flex items-center justify-between px-3"
                >
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 flex items-center justify-center text-primary-600">
                      <i className={`${platformIcons[p] ?? 'ri-download-2-line'} text-base`} />
                    </span>
                    {p}
                  </span>
                  <span className="w-4 h-4 flex items-center justify-center text-foreground-400">
                    <i className="ri-download-line text-base" />
                  </span>
                </button>
              ))}
            </div>
          </section>
        </div>
      </div>

      <section className="mt-8">
        <h2 className="font-heading text-lg font-bold text-foreground-950 flex items-center gap-2 mb-4">
          <span className="w-5 h-5 flex items-center justify-center text-primary-600">
            <i className="ri-magic-line text-xl" />
          </span>
          相关推荐软件
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {recommend.map((s) => (
            <SoftwareCard key={s.id} software={s} variant="grid" />
          ))}
        </div>
      </section>

      <div className="mt-8 flex items-center justify-center gap-3">
        <Link
          to="/softwares"
          className="h-10 px-5 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-700 whitespace-nowrap cursor-pointer hover:border-primary-300 transition-colors flex items-center gap-2"
        >
          <span className="w-4 h-4 flex items-center justify-center">
            <i className="ri-list-check-2 text-base" />
          </span>
          返回软件列表
        </Link>
        <button
          type="button"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="h-10 px-5 rounded-md bg-secondary-950 text-background-50 text-sm whitespace-nowrap cursor-pointer hover:bg-secondary-800 transition-colors flex items-center gap-2"
        >
          <span className="w-4 h-4 flex items-center justify-center">
            <i className="ri-arrow-up-line text-base" />
          </span>
          返回顶部
        </button>
      </div>

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] max-w-[92vw] md:max-w-lg rounded-md bg-secondary-950 text-background-50 text-sm px-4 py-3 flex items-start gap-2">
          <span className="w-4 h-4 mt-0.5 flex items-center justify-center text-primary-400 shrink-0">
            <i className="ri-information-line text-base" />
          </span>
          <span className="leading-relaxed">{toast}</span>
        </div>
      )}
    </SiteLayout>
  );
}